import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import {
  SITE_EDITABLE_STATUS_OPTIONS,
  SITE_STATUS_BADGE_CLASS,
  SITE_STATUS_LABELS,
  isSiteStatusEditable,
  type SiteEditableStatus,
  type SiteStatus,
} from '../lib/sites';
import { MEMBER_LABEL, OWNER_LABEL } from '../lib/roles';
import SiteApprovalLineTab from './approval-line-meta/SiteApprovalLineTab';
import AdminIndustryPicker from './AdminIndustryPicker';
import { siteIndustryLabel } from '../lib/siteIndustry';
import type { AdminSiteIndustry } from '../lib/api';

interface Site {
  id: string;
  name: string;
  address: string | null;
  addressDetail: string | null;
  industryType: string | null;
  industrySubType: string | null;
  industryId: string | null;
  industry: AdminSiteIndustry | null;
  status: SiteStatus;
  createdAt: string;
  organization: {
    id: string;
    name: string;
    category: string;
  } | null;
  _count: {
    inspections: number;
    assignments: number;
  };
}

interface SiteDetailModalProps {
  site: Site | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function SiteDetailModal({
  site,
  isOpen,
  onClose,
}: SiteDetailModalProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'approval-line'>('info');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [approvalLineDirty, setApprovalLineDirty] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string;
    address: string;
    addressDetail: string;
    /** 업종 분류(KSIC) `Industry.id` — 빈 문자열이면 미지정. 업종 편집의 유일한 칸(레거시 자유 텍스트는 읽기 전용) */
    industryId: string;
    status: SiteEditableStatus;
  }>({
    name: '',
    address: '',
    addressDetail: '',
    industryId: '',
    status: 'ACTIVE',
  });

  // 현장 상세 조회
  const { data: siteDetail, isLoading } = useQuery({
    queryKey: ['admin', 'sites', site?.id, 'detail'],
    queryFn: () => adminApi.getSiteDetail(site!.id),
    enabled: !!site?.id && isOpen,
  });

  const detail = siteDetail?.data;

  // 현장 수정 mutation
  const updateMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      address?: string;
      addressDetail?: string;
      industryId?: string | null;
      status?: SiteEditableStatus;
    }) => adminApi.updateSite(site!.id, data),
    onSuccess: (data) => {
      if (data.success) {
        setSuccess('현장 정보가 수정되었습니다.');
        setIsEditing(false);
        queryClient.invalidateQueries({ queryKey: ['admin', 'sites'] });
      } else {
        setError(data.error?.message || '수정에 실패했습니다.');
      }
    },
    onError: (err) => {
      const apiError = extractApiError(err);
      setError(apiError.message);
    },
  });

  // 모달이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccess(null);
      setIsEditing(false);
    }
  }, [isOpen]);

  // 상세 정보가 로드되면 수정 폼 초기화
  useEffect(() => {
    if (detail) {
      setEditForm({
        name: detail.name || '',
        address: detail.address || '',
        addressDetail: detail.addressDetail || '',
        industryId: detail.industryId || '',
        // 이관중이면 폼이 그 상태를 대신 써 보내면 안 되므로 선택지 밖 값은 담지 않는다
        // (저장 body에서도 status를 뺀다 — handleSave 참조).
        status: isSiteStatusEditable(detail.status) ? detail.status : 'ACTIVE',
      });
    }
  }, [detail]);

  // 메시지 자동 숨김
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handleSave = () => {
    setError(null);
    updateMutation.mutate({
      name: editForm.name,
      address: editForm.address || undefined,
      addressDetail: editForm.addressDetail || undefined,
      // 업종 분류 — 바뀐 때만 싣는다(그대로면 서버가 묻지도 실체화하지도 않지만 칸을 건드릴 이유가 없다).
      // 지움은 null(undefined는 서버에서 「그대로」). 레거시 자유 텍스트는 싣지 않는다 — 읽기 전용(2026-10-31)
      industryId:
        editForm.industryId !== (detail?.industryId ?? '') ? editForm.industryId || null : undefined,
      status: detail && isSiteStatusEditable(detail.status) ? editForm.status : undefined,
    });
  };

  const handleCancel = () => {
    if (detail) {
      setEditForm({
        name: detail.name || '',
        address: detail.address || '',
        addressDetail: detail.addressDetail || '',
        industryId: detail.industryId || '',
        // 이관중이면 폼이 그 상태를 대신 써 보내면 안 되므로 선택지 밖 값은 담지 않는다
        // (저장 body에서도 status를 뺀다 — handleSave 참조).
        status: isSiteStatusEditable(detail.status) ? detail.status : 'ACTIVE',
      });
    }
    setIsEditing(false);
    setError(null);
  };

  // 미저장 닫기 보호 — 「수정」 모드에서 불러온 값(위 detail 파생 — handleCancel과 같은 식)과 다를 때,
  // 또는 결재라인 탭의 카드 편집 draft가 dirty일 때(자식 state라 탭이 올려 준다 — hooks/useChildDirty).
  // 보기 모드는 적던 내용이 없다.
  const isDirty =
    approvalLineDirty ||
    (isEditing &&
    !!detail &&
    (editForm.name !== (detail.name || '') ||
      editForm.address !== (detail.address || '') ||
      editForm.addressDetail !== (detail.addressDetail || '') ||
      editForm.industryId !== (detail.industryId || '') ||
      editForm.status !== (isSiteStatusEditable(detail.status) ? detail.status : 'ACTIVE')));

  const getLoginIdentifier = (auths: Array<{ provider: string; providerId: string }>) => {
    const idAuth = auths.find((a) => a.provider === 'ID');
    const emailAuth = auths.find((a) => a.provider === 'EMAIL');
    return idAuth?.providerId || emailAuth?.providerId || '-';
  };

  if (!site) return null;

  return (
    <Modal open={isOpen} onClose={onClose} title="현장 상세 정보" size="lg" isDirty={isDirty}>
      <div className="space-y-4">
        {/* 탭 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-4">
            {[
              { key: 'info', label: '기본 정보' },
              { key: 'approval-line', label: '결재라인' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key as 'info' | 'approval-line')}
                className={`py-2 px-1 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.key
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'approval-line' ? (
          <SiteApprovalLineTab siteId={site.id} onDirtyChange={setApprovalLineDirty} />
        ) : (
        <div className="space-y-6">
        {/* 메시지 */}
        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-card">{error}</div>
        )}
        {success && (
          <div className="p-3 bg-green-50 text-green-600 text-sm rounded-card">{success}</div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">로딩 중...</div>
        ) : detail ? (
          <>
            {/* 기본 정보 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-500">기본 정보</h4>
                {!isEditing && (
                  <Button variant="primary" size="xs" onClick={() => setIsEditing(true)}>
                    수정
                  </Button>
                )}
              </div>

              {isEditing ? (
                <Card surface="soft" elevation="none" className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">현장명 *</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">주소</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">상세 주소</label>
                    <input
                      type="text"
                      value={editForm.addressDetail}
                      onChange={(e) => setEditForm({ ...editForm, addressDetail: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500 mb-1">업종 분류(한국표준산업분류)</span>
                    <AdminIndustryPicker
                      value={editForm.industryId}
                      onChange={(industryId) => setEditForm({ ...editForm, industryId })}
                      disabled={updateMutation.isPending}
                    />
                    {/* 옛 자유 텍스트 업종 — 읽기 전용 단서(KSIC를 아직 안 고른 현장에서만) */}
                    {!editForm.industryId && siteIndustryLabel({ industryType: detail.industryType, industrySubType: detail.industrySubType }) && (
                      <p className="mt-1 text-xs text-gray-500">
                        기존에 입력한 업종:{' '}
                        {siteIndustryLabel({ industryType: detail.industryType, industrySubType: detail.industrySubType })}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="site-status" className="block text-xs text-gray-500 mb-1">
                      상태
                    </label>
                    {isSiteStatusEditable(detail.status) ? (
                      <select
                        id="site-status"
                        value={editForm.status}
                        onChange={(e) =>
                          setEditForm({ ...editForm, status: e.target.value as SiteEditableStatus })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        {SITE_EDITABLE_STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      // 이관중은 이관 플로우가 소유하는 상태라 여기서 바꿀 수 없다
                      <p className="text-sm text-gray-500">
                        {SITE_STATUS_LABELS[detail.status]} — 이관 진행 중에는 변경할 수 없습니다.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={!editForm.name.trim() || updateMutation.isPending}
                    >
                      {updateMutation.isPending ? '저장 중...' : '저장'}
                    </Button>
                    <Button variant="secondary" onClick={handleCancel}>
                      취소
                    </Button>
                  </div>
                </Card>
              ) : (
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs text-gray-500">현장명</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-medium">{detail.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">상태</dt>
                    <dd className="mt-1">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs rounded-full ${SITE_STATUS_BADGE_CLASS[detail.status]}`}
                      >
                        {SITE_STATUS_LABELS[detail.status]}
                      </span>
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs text-gray-500">주소</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {[detail.address, detail.addressDetail].filter(Boolean).join(' ') || '-'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">업종</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {siteIndustryLabel(detail) ?? '-'}
                      {detail.industry && <span className="text-gray-400"> ({detail.industry.code})</span>}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">ID</dt>
                    <dd className="mt-1 text-sm text-gray-500 font-mono text-xs">{detail.id}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">생성일</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(detail.createdAt).toLocaleDateString('ko-KR')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">최종 수정일</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {new Date(detail.updatedAt).toLocaleDateString('ko-KR')}
                    </dd>
                  </div>
                </dl>
              )}
            </div>

            {/* 소속 조직 */}
            {detail.organization && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">소속 조직</h4>
                <div className="p-3 bg-gray-50 rounded-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{detail.organization.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{detail.organization.id}</div>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        detail.organization.category !== 'WORKSPACE'
                          ? 'bg-brand-100 text-brand-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {detail.organization.category !== 'WORKSPACE' ? '비즈니스' : '개인/팀'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* 통계 */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3">통계</h4>
              {/* 세 통계 타일은 색으로 값을 가른다(점검=blue·보고서=green·TBM=purple).
                  blue만 brand(인디고)로 옮기면 옆 purple과 붙어 구분이 무너지므로 유지한다. */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-blue-50 rounded-card text-center">
                  <div className="text-2xl font-bold text-blue-700">{detail._count.inspections}</div>
                  <div className="text-xs text-blue-600">점검</div>
                </div>
                <div className="p-3 bg-green-50 rounded-card text-center">
                  <div className="text-2xl font-bold text-green-700">{detail._count.reports}</div>
                  <div className="text-xs text-green-600">보고서</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-card text-center">
                  <div className="text-2xl font-bold text-purple-700">{detail._count.tbms}</div>
                  <div className="text-xs text-purple-600">TBM</div>
                </div>
              </div>
            </div>

            {/* 담당자 목록 */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3">
                담당자 ({detail.assignments.length}명)
              </h4>
              {detail.assignments.length === 0 ? (
                <div className="text-center py-4 text-gray-400 text-sm">
                  배정된 담당자가 없습니다.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-card overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          이름
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          로그인 ID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          조직 역할
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          담당 유형
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {detail.assignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {assignment.userName}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {getLoginIdentifier(assignment.auths)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                                assignment.isOwner
                                  ? 'bg-brand-100 text-brand-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {/* 소유자 성질(배정 축) — 옛 `memberRole === 'OWNER'`. 비소유자 라벨이 '안전관리자'였던 것은
                                  역할이 두 종뿐이던 시절의 어휘다. 지금 안전관리자는 시드 12종 중 하나라 여기 쓸 말이 아니다. */}
                              {assignment.isOwner ? OWNER_LABEL : MEMBER_LABEL}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                                assignment.assignmentType === 'PRIMARY'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-orange-100 text-orange-700'
                              }`}
                            >
                              {assignment.assignmentType === 'PRIMARY' ? '주담당' : '부담당'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 추가 정보 (위험 카테고리, 좌표 등) */}
            {(detail.hazardCategories.length > 0 || detail.latitude || detail.timezone) && (
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-3">추가 정보</h4>
                <dl className="space-y-3">
                  {detail.hazardCategories.length > 0 && (
                    <div>
                      <dt className="text-xs text-gray-500 mb-1">위험 카테고리</dt>
                      <dd className="flex flex-wrap gap-1">
                        {detail.hazardCategories.map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded"
                          >
                            {cat}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                  {detail.latitude && detail.longitude && (
                    <div>
                      <dt className="text-xs text-gray-500">좌표</dt>
                      <dd className="mt-1 text-sm text-gray-600 font-mono">
                        {detail.latitude.toFixed(6)}, {detail.longitude.toFixed(6)}
                      </dd>
                    </div>
                  )}
                  {detail.timezone && (
                    <div>
                      <dt className="text-xs text-gray-500">시간대</dt>
                      <dd className="mt-1 text-sm text-gray-900">{detail.timezone}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">현장 정보를 불러올 수 없습니다.</div>
        )}
      </div>
        )}
      </div>
    </Modal>
  );
}
